import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { CalendarClock, CheckCircle2, Loader2, Video } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { cn } from "@/lib/utils";
import StepShell from "../StepShell";
import * as api from "../briefingService";

/**
 * Bloco 7 — Go-live. O usuário escolhe data e horário da reunião de implantação,
 * com slots vindos do Google Calendar da equipe TouchWorks. Pré-reserva segura o
 * horário (~10min) e a confirmação cria o evento + link do Meet.
 */
/**
 * Earliest bookable kickoff date: always at least 3 days out (never today or
 * the next two days) to give the team lead time, then rolled forward off
 * weekends. The available time slots themselves come from the team's Google
 * Calendar (business hours), so those rules stay respected.
 */
function earliestKickoffDate() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  return d;
}

/** Date -> "YYYY-MM-DD" in local time (avoids a UTC off-by-one). */
function toIsoDay(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** "YYYY-MM-DD" -> "DD/MM/YYYY" (Brazilian format). */
function formatBrDate(iso) {
  if (!iso || iso.length < 10) return iso || "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

export default function Bloco7GoLive({ briefing, autosave }) {
  const projectId = briefing.projectId;
  const sessionIdRef = useRef(
    (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
      `sess-${Date.now()}`,
  );

  const [date, setDate] = useState(() => toIsoDay(earliestKickoffDate()));
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotsError, setSlotsError] = useState("");
  const [selected, setSelected] = useState(null); // "HH:MM"
  const [status, setStatus] = useState(briefing.goLiveAt ? "confirmed" : "idle"); // idle|reserving|reserved|confirming|confirmed|error
  const [meetLink, setMeetLink] = useState(briefing.goLiveMeetLink || "");
  const [errorMsg, setErrorMsg] = useState("");

  // Block anything sooner than the earliest bookable date (≥ 3 days out) plus
  // weekends in the custom calendar (no native OS picker).
  const earliest = useMemo(() => earliestKickoffDate(), []);
  const disabledDays = useMemo(
    () => [{ before: earliest }, { dayOfWeek: [0, 6] }],
    [earliest],
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoadingSlots(true);
      setSlotsError("");
      setSelected(null);
      try {
        const { slots: s } = await api.fetchSlots(date);
        if (!cancelled) setSlots(Array.isArray(s) ? s : []);
      } catch (err) {
        if (!cancelled)
          setSlotsError(
            err.status === 503
              ? "Agenda indisponível no momento. Tente novamente mais tarde."
              : err.message || "Falha ao carregar horários.",
          );
      } finally {
        if (!cancelled) setLoadingSlots(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [date]);

  async function pickSlot(time) {
    setStatus("reserving");
    setErrorMsg("");
    try {
      await api.preReserveSlot(date, time, sessionIdRef.current);
      setSelected(time);
      setStatus("reserved");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.message || "Não foi possível reservar este horário.");
    }
  }

  async function confirm() {
    if (!selected) return;
    setStatus("confirming");
    setErrorMsg("");
    try {
      const { booking } = await api.confirmGoLive(projectId, {
        date,
        time: selected,
        sessionId: sessionIdRef.current,
        name: briefing.responsibleName,
        email: briefing.responsibleEmail,
        phone: briefing.responsibleWhatsapp,
        company: briefing.responsibleName,
      });
      setMeetLink(booking?.meetLink || "");
      setStatus("confirmed");
      autosave({ goLiveAt: `${date}T${selected}:00-03:00` });
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.message || "Falha ao confirmar o agendamento.");
    }
  }

  const confirmed = status === "confirmed";

  return (
    <StepShell
      title="Agende o kickoff e treinamento"
      description="Escolha a data e o horário da reunião de implantação e treinamento com a equipe TouchWorks."
    >
      {confirmed ? (
        <div className="flex items-start gap-3 rounded-2xl border border-cyan-500/30 bg-cyan-500/[0.06] p-5">
          <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-cyan-600 dark:text-cyan-400" />
          <div>
            <p className="font-medium text-foreground">Reunião agendada!</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {formatBrDate(date)} às {selected || briefing.goLiveAt?.slice(11, 16)}.
            </p>
            {meetLink && (
              <a
                href={meetLink}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-cyan-600 hover:underline dark:text-cyan-400"
              >
                <Video className="h-4 w-4" /> Abrir Google Meet
              </a>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="mb-5 max-w-xs space-y-2">
            <Label className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-muted-foreground" /> Data
            </Label>
            <DatePicker
              value={date}
              onChange={(v) => v && setDate(v)}
              disabledDays={disabledDays}
              startMonth={earliest}
              allowClear={false}
              placeholder="Escolha uma data"
            />
          </div>

          {loadingSlots ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Buscando horários na agenda…
            </p>
          ) : slotsError ? (
            <p className="text-sm text-destructive">{slotsError}</p>
          ) : slots.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Sem horários livres nesta data. Tente outro dia.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                {slots.map((s) => {
                  const active = selected === s.time;
                  return (
                    <motion.button
                      key={s.time}
                      type="button"
                      onClick={() => pickSlot(s.time)}
                      disabled={status === "reserving" || status === "confirming"}
                      whileTap={{ scale: 0.96 }}
                      className={cn(
                        "rounded-full border px-4 py-2 text-sm font-medium transition-colors",
                        active
                          ? "border-cyan-500/70 bg-cyan-500/10 text-cyan-600 ring-2 ring-cyan-500/20 dark:text-cyan-400"
                          : "border-border bg-surface text-muted-foreground hover:border-border-strong",
                      )}
                    >
                      {s.time}
                    </motion.button>
                  );
                })}
              </div>
            </>
          )}

          {selected && (
            <div className="mt-6 flex items-center gap-3">
              <Button onClick={confirm} disabled={status === "confirming"}>
                {status === "confirming" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Confirmar {formatBrDate(date)} às {selected}
              </Button>
              {status === "reserved" && (
                <span className="text-xs text-muted-foreground">
                  Horário reservado por ~10 min — confirme para garantir.
                </span>
              )}
            </div>
          )}

          {status === "error" && (
            <p className="mt-3 text-sm text-destructive">{errorMsg}</p>
          )}
        </>
      )}
    </StepShell>
  );
}
